import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Plus, Search, Download, Eye, Trash2, Edit, Shield, Star, Camera, Share2, Cpu } from 'lucide-react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '../../hooks/useAuth'
import { modelsApi } from '../../services/api/models'
import { VideoPreview } from '../../components/VideoPreview'
import PermissionsModal from '../../components/admin/PermissionsModal'
import type { Model, ModelFilters } from '../../types/models'

export default function AdminModels() {
  const { token, adminEmail } = useAuth()
  const queryClient = useQueryClient()
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCategory, setSelectedCategory] = useState<string>('')
  const [selectedModels, setSelectedModels] = useState<string[]>([])
  const [offset, setOffset] = useState(0)
  const [permissionsModal, setPermissionsModal] = useState<{ model: Model | null; isOpen: boolean }>({ model: null, isOpen: false })

  const filters: ModelFilters = {
    search: searchTerm || undefined,
    category: selectedCategory || undefined,
    visibility: 'all',
    sort: 'newest',
    offset,
  }

  const { data: modelsData, isLoading } = useQuery({
    queryKey: ['admin-models', filters],
    queryFn: () => modelsApi.getModels(filters, token!)
  })

  const { data: categoriesData } = useQuery({
    queryKey: ['categories'],
    queryFn: () => modelsApi.getCategories()
  })

  const deleteModelMutation = useMutation({
    mutationFn: async (modelId: string) => {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/v2/admin/models/${modelId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Admin-Email': adminEmail!
        }
      })
      if (!response.ok) throw new Error('Failed to delete model')
      return response.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-models'] })
    }
  })

  const setFeaturedMutation = useMutation({
    mutationFn: async ({ modelId, featured }: { modelId: string; featured: boolean }) => {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/v2/admin/models/${modelId}/featured`, {
        method: featured ? 'POST' : 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Admin-Email': adminEmail!
        }
      })
      if (!response.ok) throw new Error('Failed to update featured status')
      return response.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-models'] })
    }
  })

  const handleDelete = async (modelId: string) => {
    if (confirm('Are you sure you want to delete this model?')) {
      await deleteModelMutation.mutateAsync(modelId)
    }
  }

  const handleBulkDelete = async () => {
    if (confirm(`Delete ${selectedModels.length} selected models?`)) {
      await Promise.all(selectedModels.map(id => deleteModelMutation.mutateAsync(id)))
      setSelectedModels([])
    }
  }

  const toggleSelection = (modelId: string) => {
    setSelectedModels(prev => 
      prev.includes(modelId) 
        ? prev.filter(id => id !== modelId)
        : [...prev, modelId]
    )
  }

  const models = modelsData?.data || []
  const categories = categoriesData?.data || []

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-4 flex-1">
                <div className="relative flex-1 max-w-md">
                  <input
                    type="text"
                    placeholder="Search models..."
                    value={searchTerm}
                    onChange={(e) => { setSearchTerm(e.target.value); setOffset(0) }}
                    className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                  <Search className="absolute left-3 top-2.5 w-5 h-5 text-gray-400" />
                </div>

                <select
                  value={selectedCategory}
                  onChange={(e) => { setSelectedCategory(e.target.value); setOffset(0) }}
                  className="border rounded-lg px-4 py-2 focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="">All Categories</option>
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.slug}>{cat.name}</option>
                  ))}
                </select>
              </div>

              <div className="flex items-center gap-3">
                {selectedModels.length > 0 && (
                  <button
                    onClick={handleBulkDelete}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                  >
                    Delete ({selectedModels.length})
                  </button>
                )}
                <Link
                  to="/admin/models/solve"
                  className="flex items-center gap-2 px-4 py-2 border border-indigo-600 text-indigo-600 rounded-lg hover:bg-indigo-50 transition-colors"
                >
                  <Cpu className="w-5 h-5" />
                  Cloud Solve
                </Link>
                <Link
                  to="/admin/models/new"
                  className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                >
                  <Plus className="w-5 h-5" />
                  Add Model
                </Link>
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-6 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={selectedModels.length === models.length && models.length > 0}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedModels(models.map(m => m.id))
                        } else {
                          setSelectedModels([])
                        }
                      }}
                      className="rounded"
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Model
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Category
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Visibility
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Stats
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {isLoading ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                      Loading models...
                    </td>
                  </tr>
                ) : models.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                      No models found
                    </td>
                  </tr>
                ) : (
                  models.map((model) => (
                    <tr key={model.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <input
                          type="checkbox"
                          checked={selectedModels.includes(model.id)}
                          onChange={() => toggleSelection(model.id)}
                          className="rounded"
                        />
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          <div className="h-10 w-10 flex-shrink-0 relative group">
                            {model.previewVideoPath ? (
                              <div className="h-10 w-10 rounded-lg overflow-hidden">
                                <VideoPreview
                                  src={`${import.meta.env.VITE_API_URL}/api/v2/models/${model.id}/media?path=${encodeURIComponent(model.previewVideoPath)}`}
                                  poster={
                                    model.thumbnailPath
                                      ? `${import.meta.env.VITE_API_URL}/api/v2/models/${model.id}/thumbnail?size=256`
                                      : undefined
                                  }
                                  className="w-full h-full object-cover"
                                />
                              </div>
                            ) : (
                              <img
                                className="h-10 w-10 rounded-lg object-cover"
                                src={
                                  model.thumbnailPath
                                    ? `${import.meta.env.VITE_API_URL}/api/v2/models/${model.id}/thumbnail?size=256`
                                    : `${import.meta.env.VITE_API_URL}/api/placeholder/256/256`
                                }
                                alt={model.name}
                                onError={(e) => {
                                  const target = e.target as HTMLImageElement
                                  target.src = `${import.meta.env.VITE_API_URL}/api/placeholder/256/256`
                                }}
                              />
                            )}
                          </div>
                          <div className="ml-4">
                            <div className="flex items-center gap-2">
                              <div className="text-sm font-medium text-gray-900">{model.name}</div>
                              {model.featured && (
                                <span className="inline-flex px-2 py-0.5 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">
                                  Featured
                                </span>
                              )}
                            </div>
                            <div className="text-sm text-gray-500">
                              {(model.fileSize / (1024 * 1024)).toFixed(1)} MB
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {model.category}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          model.visibility === 'public' 
                            ? 'bg-green-100 text-green-800'
                            : model.visibility === 'private'
                            ? 'bg-red-100 text-red-800'
                            : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {model.visibility}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        <div className="flex items-center gap-4">
                          <span className="flex items-center gap-1">
                            <Eye className="w-4 h-4" />
                            {model.viewCount}
                          </span>
                          <span className="flex items-center gap-1">
                            <Download className="w-4 h-4" />
                            {model.downloadCount}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <Link
                            to={`/admin/models/${model.id}/edit`}
                            className="text-indigo-600 hover:text-indigo-900"
                            title="Edit"
                          >
                            <Edit className="w-5 h-5" />
                          </Link>
                          <Link
                            to={`/admin/models/${model.id}/thumbnail`}
                            state={{ from: '/admin/models' }}
                            className="text-green-600 hover:text-green-900"
                            title="Thumbnail Generator"
                          >
                            <Camera className="w-5 h-5" />
                          </Link>
                          <button
                            onClick={() => setFeaturedMutation.mutate({ modelId: model.id, featured: !model.featured })}
                            className={`${
                              model.featured 
                                ? 'text-yellow-600 hover:text-yellow-700' 
                                : 'text-gray-400 hover:text-gray-600'
                            }`}
                            title={model.featured ? 'Remove from featured' : 'Set as featured'}
                          >
                            <Star className="w-5 h-5" fill={model.featured ? 'currentColor' : 'none'} />
                          </button>
                          {model.visibility === 'private' && (
                            <button
                              onClick={() => setPermissionsModal({ model, isOpen: true })}
                              className="text-purple-600 hover:text-purple-900"
                              title="Permissions"
                            >
                              <Shield className="w-5 h-5" />
                            </button>
                          )}
                          {model.visibility === 'unlisted' && (
                            <Link
                              to={`/admin/models/${model.id}/edit`}
                              className="text-indigo-600 hover:text-indigo-900"
                              title="Manage share links"
                            >
                              <Share2 className="w-5 h-5" />
                            </Link>
                          )}
                          <button
                            onClick={() => handleDelete(model.id)}
                            className="text-red-600 hover:text-red-900"
                            title="Delete"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {modelsData?.pagination && modelsData.pagination.total > modelsData.pagination.limit && (
            <div className="px-6 py-4 border-t flex items-center justify-between">
              <p className="text-sm text-gray-700">
                Showing {modelsData.pagination.offset + 1} to{' '}
                {Math.min(modelsData.pagination.offset + modelsData.pagination.limit, modelsData.pagination.total)}{' '}
                of {modelsData.pagination.total} models
              </p>
              <div className="flex gap-2">
                <button
                  disabled={modelsData.pagination.offset === 0}
                  onClick={() => setOffset(Math.max(0, modelsData.pagination.offset - modelsData.pagination.limit))}
                  className="px-4 py-2 border rounded-lg disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  disabled={!modelsData.pagination.hasMore}
                  onClick={() => setOffset(modelsData.pagination.offset + modelsData.pagination.limit)}
                  className="px-4 py-2 border rounded-lg disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>

      {permissionsModal.model && (
        <PermissionsModal
          model={permissionsModal.model}
          isOpen={permissionsModal.isOpen}
          onClose={() => setPermissionsModal({ model: null, isOpen: false })}
        />
      )}
    </div>
  )
}